// @flow
import { AdSlot, GraphController, Grid, GridItem, MobileGraphController, Query, SeoData, useMedia, } from '@haaretz/htz-components';
import React, { Fragment, } from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import AssetStaticGraphs from '../../components/AssetStaticGraphs/AssetStaticGraphs';
import PageRow from '../../components/PageRow/PageRow';
import QuoteInfoTable from '../../components/QuotePageComponents/QuoteInfoTable/QuoteInfoTable';
import QuoteSummary from '../../components/QuotePageComponents/QuoteSummary/QuoteSummary';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import MtfQuery from '../queries/mtfData.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      assetId: string,
      section: string,
    },
    asPath: string,
  },
  responseEnd: () => void,
  responseWriteHead: (statusCode: number, headers: { [key: string]: string | number | boolean, }) => void,
};

function mtf({
  url: {
    query: { section, assetId, },
    asPath,
  },
  responseEnd,
  responseWriteHead,
}: Props): Node {
  const isMobile = useMedia({ query: { from: 's', }, });
  const adUnits = useAllAdUnits();
  const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <Query query={MtfQuery} variables={{ assetId, }}>
        {({ loading, error, data, }) => {
          if (error) return null;
          if (loading) return null;
          if (!data.assets?.length) return null;

          const type = data.assets[0]?.type?.toLowerCase();
          if (type && section !== type) {
            responseWriteHead(301, { Location: encodeURI(asPath.replace(section, type)), });
            responseEnd();

            return null;
          }

          const {
            name,
            value,
            changePercentage,
            subType,
            assetNumber,
            lastTradeTime,
            mtfEtfPolicy,
            policyChangeDate,
            ...staticGraphsData
          } = data.assets[0];
          return (
            <FelaTheme>
              {theme => (
                <Fragment>
                  <SeoData
                    title={`${name} - קרנות נאמנות - TheMarker Finance`}
                    description={`${name} - למידע עדכני על נתוני קרנות נאמנות, היכנסו לאתר TheMarker Finance`}
                    path={asPath}
                  />
                  <PageRow lines={2}>
                    <RowItem
                      title={name}
                      miscStyles={{
                        ...theme.type(5),
                      }}
                    />
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      marginBottom: '2rem',
                    }}
                  >
                    <QuoteSummary
                      valueData={[
                        { title: 'שער', value: value ? value.toString() : '', },
                        { title: '% שינוי', value: changePercentage, },
                      ]}
                      date={{ title: 'נכון ל:', value: lastTradeTime, }}
                      assetInfo={[
                        { title: 'סוג נייר:', value: subType, },
                        { title: 'מספר נייר:', value: assetNumber, },
                      ]}
                    />
                  </PageRow>
                  <PageRow>
                    {
                      isMobile
                        ? (
                          <GraphController
                            assetId={assetId}
                            width={900}
                            periods={[
                              { value: 'week', display: 'שבועי', },
                              { value: 'month', display: 'חודשי', },
                              { value: 'year1', display: 'שנתי', },
                              { value: 'year3', display: '3 שנים', },
                              { value: 'year5', display: 'מקסימום', },
                            ]}
                          />
                        )
                        : <MobileGraphController assetId={assetId} />
                    }
                  </PageRow>
                  <PageRow>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <QuoteInfoTable
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 3, },
                        ]}
                        title="נתוני המסחר"
                        id={assetId}
                        tradingStatus
                        fixed
                        fields={[
                          { name: 'purchasePrice', display: 'מחיר קנייה', },
                          { name: 'redemptionPrice', display: 'מחיר פדיון', },
                          { name: 'changePercentage', display: 'שינוי ב-%', },
                          { name: 'lastTradeTime', display: 'מועד עדכון אחרון', type: 'date', },
                          { name: 'standardDeviation', display: 'סטיית תקן', },
                          { name: 'sharpIndex', display: 'שארפ', },
                        ]}
                      />
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 2 / 3, },
                        ]}
                      >
                        <AssetStaticGraphs {...staticGraphsData} />
                      </GridItem>
                    </Grid>
                  </PageRow>
                  <PageRow>
                    <RowItem title="מידע כללי" miscStyles={{ marginBottom: '2rem', }}>
                      <Grid
                        gutter={2}
                        miscStyles={{
                          paddingStart: '0rem',
                          paddingEnd: '0rem',
                        }}
                      >
                        <QuoteInfoTable
                          id={assetId}
                          width={[
                            { until: 'l', value: 1, },
                            { from: 'l', value: 1 / 3, },
                          ]}
                          fields={[
                            { name: 'manager', display: 'מנהל הקרן', },
                            { name: 'trustee', display: 'נאמן הקרן', },
                            { name: 'exposureProfile', display: 'פרופיל חשיפה', },
                            { name: 'foundingDate', display: 'תאריך הקמה', type: 'date', },
                            { name: 'tradingHours', display: 'שעות מסחר', },
                            { name: 'dividendPolicy', display: 'סיווג דיבידנד', },
                          ]}
                        />
                        <QuoteInfoTable
                          id={assetId}
                          width={[
                            { until: 'l', value: 1, },
                            { from: 'l', value: 1 / 3, },
                          ]}
                          fields={[
                            { name: 'mtfLinkForeignExchange', display: 'צמוד מט״ח', },
                            { name: 'mtfLinkIndex', display: 'צמוד מדד', },
                            { name: 'mtfLinkShekel', display: 'צמוד שקל', },
                            { name: 'mtfLinkStocks', display: 'חשיפה למניות', },
                            { name: 'mtfLinkStockForeign', display: 'חשיפה למניות חו"ל', },
                            { name: 'mtfLinkBondForeign', display: 'חשיפה לאג"ח חו"ל', },
                          ]}
                        />
                        <QuoteInfoTable
                          id={assetId}
                          width={[
                            { until: 'l', value: 1, },
                            { from: 'l', value: 1 / 3, },
                          ]}
                          fields={[
                            { name: 'managementFee', display: 'דמי ניהול', },
                            { name: 'trusteeFee', display: 'דמי נאמנות', },
                            { name: 'loadChargeRate', display: 'שיעורי הוספה', },
                            { name: 'distributionCommission', display: 'עמלת הפצה', },
                            { name: 'mainCurrency', display: 'מטבע עיקרי', },
                          ]}
                        />
                      </Grid>
                    </RowItem>
                  </PageRow>
                  <PageRow>
                    <RowItem>
                      <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
                    </RowItem>
                  </PageRow>
                  <PageRow>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 2, },
                        ]}
                      >
                        <RowItem title="מדיניות" miscStyles={{ marginBottom: '1rem', }}>
                          <FelaComponent
                            as="p"
                            style={{
                              ...theme.type(-1),
                              color: theme.color('neutral', '-3'),
                              marginBottom: '1rem',
                              marginTop: '1rem',
                              textAlign: 'start',
                            }}
                          >
                            {`תאריך שינוי מדיניות: ${new Date(policyChangeDate).toLocaleString(
                              'it-It',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }
                            )}`}
                          </FelaComponent>
                          <FelaComponent
                            style={{
                              ...theme.type(-1),
                            }}
                            as="p"
                          >
                            {mtfEtfPolicy}
                          </FelaComponent>
                        </RowItem>
                      </GridItem>
                      <QuoteInfoTable
                        rowMiscStyles={{ marginBottom: '2rem', }}
                        id={assetId}
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 2, },
                        ]}
                        title="סיווגים"
                        fixed
                        fields={[
                          { name: 'primeClassification', display: 'סיווג על', },
                          { name: 'mainClassification', display: 'סיווג ראשי', },
                          { name: 'secondaryClassification', display: 'סיווג משני', },
                          { name: 'taxClassification', display: 'סיווג מס', },
                          { name: 'stocksExposure', display: 'חשיפה למניות', },
                          { name: 'currencyExposure', display: 'חשיפה למט"ח', },
                        ]}
                      />
                    </Grid>
                  </PageRow>
                </Fragment>
              )}
            </FelaTheme>
          );
        }}
      </Query>
    </MainLayout>
  );
}

export default mtf;
